import React, { useState } from "react";
import { Plus, X, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/format";
import { toast } from "sonner";

export function CookieBuilder({ cookies, promoLabel, pairPrice = 6, pairSize = 2, bundleProductId }) {
  const { addItem } = useCart();
  const [pairs, setPairs] = useState([[]]); // array of pairs, each pair = array of flavor objects
  const available = cookies.filter((c) => c.status !== "sold_out" && !c.hidden);

  const pick = (pairIdx, cookie) => {
    setPairs((prev) => {
      const copy = prev.map((p) => [...p]);
      const pair = copy[pairIdx];
      if (pair.length >= pairSize) {
        pair[pairSize - 1] = cookie; // replace last
      } else {
        pair.push(cookie);
      }
      return copy;
    });
  };

  const clearSlot = (pairIdx, slotIdx) => {
    setPairs((prev) => {
      const copy = prev.map((p) => [...p]);
      copy[pairIdx].splice(slotIdx, 1);
      return copy;
    });
  };

  const addPair = () => setPairs((prev) => [...prev, []]);
  const removePair = (idx) => setPairs((prev) => prev.filter((_, i) => i !== idx));

  const completePairs = pairs.filter((p) => p.length === pairSize);
  const total = completePairs.length * pairPrice;

  const addToCart = () => {
    if (completePairs.length === 0) {
      toast.error(`Complete at least one pair (pick ${pairSize} flavors)`);
      return;
    }
    completePairs.forEach((pair, i) => {
      const flavors = pair.map((c) => c.name);
      addItem({
        key: `cookie__${flavors.join("_")}__${Date.now()}_${i}`,
        product_id: bundleProductId,
        category: "cookie",
        name: "Cookie Pair (Mix & Match)",
        image: pair[0].image,
        size_label: flavors.join(" + "),
        unit_price: pairPrice,
        quantity: 1,
        cookie_flavors: flavors,
      });
    });
    toast.success(`${completePairs.length} cookie pair(s) added!`);
    setPairs([[]]);
  };

  return (
    <div data-testid="cookie-builder">
      <div className="text-center mb-8">
        <span className="btn-gold inline-block px-5 py-2 rounded-full font-bold text-sm tracking-wide" data-testid="cookie-promo-label">
          {promoLabel || `${pairSize} FOR ${money(pairPrice)} — MIX & MATCH ANY FLAVOR`}
        </span>
      </div>

      {/* Flavor palette */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-10">
        {available.map((c) => (
          <button
            key={c.id}
            onClick={() => {
              const target = pairs.findIndex((p) => p.length < pairSize);
              pick(target >= 0 ? target : pairs.length - 1, c);
            }}
            data-testid={`cookie-flavor-${c.id}`}
            className="gold-border-card rounded-xl overflow-hidden bg-card-surface group text-left"
          >
            <div className="aspect-square overflow-hidden relative">
              <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {c.status === "low_stock" && (
                <span className="absolute top-1.5 left-1.5 bg-amber-600/90 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">LOW</span>
              )}
            </div>
            <div className="p-2">
              <p className="text-cream text-xs font-semibold leading-tight truncate">{c.name}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Pair builders */}
      <div className="max-w-2xl mx-auto space-y-4">
        {pairs.map((pair, pi) => (
          <div key={pi} className="gold-border-card rounded-xl bg-card-surface p-4" data-testid={`cookie-pair-${pi}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-serif text-gold">Pair #{pi + 1}</span>
              {pairs.length > 1 && (
                <button onClick={() => removePair(pi)} className="text-cream/40 hover:text-destructive text-xs" data-testid={`remove-pair-${pi}`}>
                  Remove pair
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[0, 1].map((slot) => {
                const c = pair[slot];
                return (
                  <div key={slot} className="rounded-lg border border-dashed border-gold/30 p-3 min-h-[76px] flex items-center gap-2" data-testid={`cookie-pair-${pi}-slot-${slot}`}>
                    {c ? (
                      <>
                        <img src={c.image} alt={c.name} className="h-12 w-12 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-cream/50">Cookie #{slot + 1}</p>
                          <p className="text-cream text-xs font-semibold truncate">{c.name}</p>
                        </div>
                        <button onClick={() => clearSlot(pi, slot)} className="text-cream/40 hover:text-destructive">
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <p className="text-cream/40 text-xs w-full text-center">Tap a flavor for Cookie #{slot + 1}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <button onClick={addPair} className="w-full rounded-xl border border-gold/30 border-dashed py-3 text-cream/70 hover:text-gold hover:border-gold/60 flex items-center justify-center gap-2 text-sm" data-testid="add-another-pair">
          <Plus size={16} /> Add another pair (+{money(pairPrice)})
        </button>
      </div>

      <div className="max-w-2xl mx-auto mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 gold-border-card rounded-xl bg-burgundy/40 p-5">
        <div>
          <p className="text-cream/60 text-sm">{completePairs.length} complete pair(s) · {completePairs.length * pairSize} cookies</p>
          <p className="font-mono text-gold text-2xl font-semibold" data-testid="cookie-builder-total">{money(total)}</p>
        </div>
        <Button onClick={addToCart} disabled={completePairs.length === 0} className="btn-gold rounded-full px-8 py-6 font-semibold disabled:opacity-40 w-full sm:w-auto" data-testid="cookie-add-to-cart">
          <ShoppingBag size={16} className="mr-2" /> Add to Cart
        </Button>
      </div>
    </div>
  );
}
